import React, { useState, useEffect, SyntheticEvent } from 'react';
import { Popover, Button, Modal, StepProps, PopoverProps, Steps } from 'antd';
import cx from 'classnames';

import {
  AdditionalInfoResponseModel,
  DEFAULT_ACTION_BTN_TEXT,
  DEFAULT_ACTION_FORBIDDEN_MESSAGE,
  GeneralProgressStep,
  isHardStopStage,
  isMarkedAsStopStage,
  isMemberResponsible,
  isSoftStopHalfCompleted,
  isSoftStopStage,
  isStageCompleted,
  isStageConsideredAsCompleted,
} from 'utils/profileProgressUtils/generalProgressUtils';
import RequestAdditionalInfoModal, {
  RequestInfo,
} from 'components/wrappers/BaseProgressBar/RequestAdditionalInfoModal';
import {
  StepChangeRequestModel,
  ProfileStatus,
} from 'utils/profileProgressUtils/generalProgressUtils';
import BaseAlert from 'components/wrappers/BaseAlert';
import { StageStatusEnum } from 'api/adminApiClient';
import { StepValidationStatuses, UserRole } from 'enums';
import RespondToRequestModal from '../RespondToRequestModal';
import AdditionalInfoViewModal from '../AdditionalInfoViewModal';
import {
  ERROR_STEP_ICON,
  getStageNumber,
  getStepIcon,
  SUCCESS_STEP_ICON,
} from 'utils/trackerUtils';

import styles from './BaseProgressStep.module.less';

interface Props extends GeneralProgressStep, StepProps {
  isAdmin?: boolean;
  index: number;
  stage: ProfileStatus;
  popoverProps?: PopoverProps;
  stageStatus: StageStatusEnum;
  onRequestOpenErrorMsg?: string;
  message: string | React.ReactNode;
  extraActionButton?: React.ReactNode;
  stepValidationStatus: StepValidationStatuses;
  responsibleRole: UserRole.Admin | UserRole.Users;
  onPulsatingHover?: (stage: ProfileStatus) => Promise<void>;
  onComplete?: (stageData: StepChangeRequestModel) => Promise<void>;
  onRequestSent?: (requestInfo: StepChangeRequestModel) => Promise<void>;
  onResponseSent?: (responseInfo: AdditionalInfoResponseModel) => Promise<void>;
  isDisplayPopover?: boolean;
}

const { Step } = Steps;

const getStepStatusClassNames = (stageStatus: StageStatusEnum) => {
  return {
    completed: isStageConsideredAsCompleted(stageStatus),
    warning: isSoftStopStage(stageStatus),
  };
};

export default React.memo((props: Props) => {
  const {
    title,
    index,
    isAdmin = false,
    onComplete,
    message: stageMessage,
    stage,
    stageStatus,
    onRequestSent,
    onResponseSent,
    isActionEnabled,
    responsibleRole,
    extraActionButton,
    onRequestOpenErrorMsg,
    requestMessage,
    responseMessage,
    onPulsatingHover,
    popoverProps,
    isPulsating = false,
    stepValidationStatus,
    displayRequestButton = false,
    displayResponseButton = false,
    actionBtnText = DEFAULT_ACTION_BTN_TEXT,
    errorText = DEFAULT_ACTION_FORBIDDEN_MESSAGE,
    isDisplayPopover = true,
    ...rest
  } = props;
  const { status, ...stepProps } = rest;
  const [isAdditionalInfoViewModalOpen, setIsAdditionalInfoViewModalOpen] =
    useState(false);
  const [isPulsatingLoading, setIsPulsatingLoading] = useState(false);
  const [isStatusChanging, setIsStatusChanging] = useState(false);
  const [isResponseModalOpen, setIsResponseModalOpen] = useState(false);
  const [isRequestModalOpen, setIsRequestModalOpen] = useState(false);
  const [stepIcon, setStepIcon] = useState(getStepIcon(stageStatus));
  const isSoftStop = isSoftStopStage(stageStatus);
  const isHardStop = isHardStopStage(stageStatus);
  const isHalfSoftStop = isSoftStopHalfCompleted(stageStatus);
  const isStopStage = isMarkedAsStopStage(stageStatus);
  const isCompleted = isStageCompleted(stageStatus);
  // https://mybasepay.atlassian.net/browse/MBO-825
  // rerender popover when step is completed
  const popoverKey = `popover-iscompleted-${isCompleted}`;

  useEffect(() => {
    const stepIcon = getStepIcon(stageStatus);

    setStepIcon(stepIcon);
  }, [isActionEnabled, stageStatus]);

  const handleRequestSend = async (requestInfo: RequestInfo) => {
    if (!onRequestSent) return;

    const { requestType, message } = requestInfo;

    const data: StepChangeRequestModel = {
      stage: stage,
      stageStatus: requestType,
      message,
    };

    await onRequestSent(data);
  };

  const handleResponseSend = async (responseMessage: string) => {
    if (!onResponseSent) return;

    const responseInfo: AdditionalInfoResponseModel = {
      responseMessage,
      stage,
    };

    await onResponseSent(responseInfo);
  };

  const handleRequestModalOpen = () => {
    if (onRequestOpenErrorMsg) {
      return BaseAlert.error(onRequestOpenErrorMsg);
    }

    if (!!requestMessage) {
      return Modal.confirm({
        title:
          'If you send a new request, your previous message will be rewritten and won’t be displayed. Do you want to proceed?',
        icon: <span className="icon icon-attention-filled" />,
        onOk() {
          setIsRequestModalOpen(true);
        },
        onCancel() {},
      });
    }

    setIsRequestModalOpen(true);
  };

  const handleRequestModalClose = () => setIsRequestModalOpen(false);

  const handleResponseModalOpen = (e: SyntheticEvent) => {
    e.preventDefault();

    setIsResponseModalOpen(true);
  };

  const handleResponseModalClose = () => setIsResponseModalOpen(false);

  const handleAdditionalInfoViewModalOpen = (e: SyntheticEvent) => {
    e.preventDefault();

    if (!requestMessage && !responseMessage) return;

    setIsAdditionalInfoViewModalOpen(true);
  };

  const handleAdditionalInfoViewModalClose = () =>
    setIsAdditionalInfoViewModalOpen(false);

  const handleStopStage = () => {
    if (!isAdmin) {
      return handleCompleteStage(StageStatusEnum.SoftStopHalfCompleted);
    }

    return Modal.confirm({
      title: 'Have you received requested information?',
      icon: <span className="icon icon-attention-filled" />,
      okText: 'Yes',
      cancelText: 'No',
      onOk() {
        handleCompleteStage(StageStatusEnum.Completed);
      },
      onCancel() {
        isSoftStop
          ? handleCompleteStage(StageStatusEnum.SoftStopHalfCompleted)
          : BaseAlert.error(
              'You cannot complete this stage unless you confirm that you received requested information.',
            );
      },
    });
  };

  const handleCompleteStage = async (stageStatus: StageStatusEnum) => {
    if (!onComplete || isStatusChanging) return;

    setIsStatusChanging(true);

    const data: StepChangeRequestModel = {
      stage,
      stageStatus,
    };

    isAdmin &&
      stageStatus === StageStatusEnum.SoftStopHalfCompleted &&
      (data.message = requestMessage);

    await onComplete(data);
    setIsStatusChanging(false);
  };

  const handleActionClick = () => {
    isSoftStop || (isHardStop && isAdmin)
      ? handleStopStage()
      : handleCompleteStage(StageStatusEnum.Completed);
  };

  const handleInformationReceived = () => {
    switch (stageStatus) {
      case StageStatusEnum.SoftStopHalfCompleted:
        return handleCompleteStage(StageStatusEnum.Completed);

      case StageStatusEnum.HardStop:
        return handleCompleteStage(StageStatusEnum.Pending);
    }
  };

  const handleHoverIconChange = (icon: string) =>
    !isActionEnabled && setStepIcon(icon);

  const renderRequestButton = () => (
    <Button
      size="small"
      type="primary"
      loading={isStatusChanging}
      disabled={isStatusChanging}
      onClick={handleRequestModalOpen}
    >
      Request additional info
    </Button>
  );

  const renderResponseButton = () => (
    <>
      Admin has requested additional information.{' '}
      <a href="/" onClick={handleResponseModalOpen}>
        Please review.
      </a>
    </>
  );

  const renderStageMessage = () => {
    if (displayResponseButton) {
      return renderResponseButton();
    }

    if (!responseMessage) {
      return stageMessage;
    }

    return (
      <div>
        {isAdmin ? (
          <>
            User has provided you with a reply.{' '}
            <a href="/" onClick={handleAdditionalInfoViewModalOpen}>
              Please review.
            </a>
          </>
        ) : (
          <>
            Response sent. Waiting for admin to respond.{' '}
            <a href="/" onClick={handleAdditionalInfoViewModalOpen}>
              View your reply.
            </a>
          </>
        )}
      </div>
    );
  };

  const renderPopoverContent = () => {
    switch (stageStatus) {
      case StageStatusEnum.Pending:
        return (
          <div className={styles.content}>
            <span>{isActionEnabled ? stageMessage : errorText}</span>
          </div>
        );

      default:
        return (
          <div className={styles.content}>
            <div>{renderStageMessage()}</div>
          </div>
        );
    }
  };

  const renderPopover = () => {
    if (isCompleted) {
      return (
        <div className={styles.content}>
          <span>{stageMessage}</span>
        </div>
      );
    }

    if (
      !isActionEnabled &&
      !displayRequestButton &&
      !displayResponseButton &&
      !isStopStage
    ) {
      return (
        <div className={styles.content}>
          <span>{errorText}</span>
        </div>
      );
    }

    return (
      <>
        {renderPopoverContent()}

        <div className={styles.action}>
          {displayRequestButton && <>{renderRequestButton()}</>}

          {isActionEnabled && (
            <>
              {extraActionButton && extraActionButton}

              {isHalfSoftStop ||
              (isHardStop &&
                isAdmin &&
                isMemberResponsible(responsibleRole)) ? (
                <Button
                  size="small"
                  type="primary"
                  loading={isStatusChanging}
                  disabled={isStatusChanging}
                  onClick={handleInformationReceived}
                >
                  Information received
                </Button>
              ) : (
                <Button
                  size="small"
                  type="primary"
                  loading={isStatusChanging}
                  disabled={isStatusChanging}
                  onClick={handleActionClick}
                >
                  {actionBtnText}
                </Button>
              )}
            </>
          )}
        </div>
      </>
    );
  };

  const handleVisibleChange = (visible: boolean) => {
    if (visible && isPulsating && !isPulsatingLoading) {
      return handlePulsatingHover();
    }

    if (isCompleted || isStopStage) return;

    handleHoverIconChange(visible ? ERROR_STEP_ICON : SUCCESS_STEP_ICON);
  };

  const handlePulsatingHover = async () => {
    if (!onPulsatingHover) return;

    setIsPulsatingLoading(true);
    await onPulsatingHover(stage);
    setIsPulsatingLoading(false);
  };

  const renderStepComponent = () => (
    <Step
      className={cx(getStepStatusClassNames(stageStatus), {
        'is-error': stepIcon === ERROR_STEP_ICON,
      })}
      status={stepValidationStatus}
      icon={
        <>
          {isPulsating && (
            <div className={styles.pulsatingContainer}>
              <div className={styles.pulsatingCircle}>
                <div className={styles.pulsatingAnimation} />
              </div>
            </div>
          )}

          <span
            className={cx('icon', stepIcon, {
              [styles.halfCompletedIcon]: isHalfSoftStop,
            })}
          />
        </>
      }
      title={
        <div>
          <div>{getStageNumber(index)}</div>
          <div className={styles.title}>{title}</div>
        </div>
      }
      {...stepProps}
    />
  );

  return (
    <>
      {isRequestModalOpen && (
        <RequestAdditionalInfoModal
          onRequestSent={handleRequestSend}
          onClose={handleRequestModalClose}
        />
      )}

      {isResponseModalOpen && requestMessage && (
        <RespondToRequestModal
          requestMessage={requestMessage}
          onResponseSent={handleResponseSend}
          onClose={handleResponseModalClose}
        />
      )}

      {isAdditionalInfoViewModalOpen && (
        <AdditionalInfoViewModal
          responseMessage={responseMessage}
          requestMessage={requestMessage}
          onClose={handleAdditionalInfoViewModalClose}
        />
      )}

      {isDisplayPopover ? (
        <Popover
          key={popoverKey}
          placement="top"
          trigger={['hover', 'focus']}
          onOpenChange={handleVisibleChange}
          overlayClassName={styles.popoverOverlay}
          content={
            <div className={styles.popoverWrapper}>{renderPopover()}</div>
          }
          {...popoverProps}
        >
          {renderStepComponent()}
        </Popover>
      ) : (
        renderStepComponent()
      )}
    </>
  );
});
